import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Crown,
  Star,
  Zap,
  Check,
  Loader2,
  ArrowLeft,
  AlertCircle,
  Shield,
  Camera,
  Sparkles,
  Package,
  Search,
  Infinity,
  CreditCard,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TODO: Set REACT_APP_STRIPE_PUBLISHABLE_KEY in frontend .env files
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Stable across renders — Stripe warns if options.appearance identity changes
const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#1E3A5F',
    fontFamily: 'Manrope, system-ui, sans-serif',
    borderRadius: '8px',
  },
};

const ICON_MAP = {
  Star, Zap, Crown, Shield, Camera, Sparkles, Package, Search, Infinity,
};

const BRAND_LABEL = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  diners: 'Diners',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function brandLabel(brand) {
  return BRAND_LABEL[(brand || '').toLowerCase()] || 'Card';
}

// ─────────────────────────────────────────────────────────────────────────────
// Payment form — picks saved card or collects a new one via PaymentElement
// ─────────────────────────────────────────────────────────────────────────────

function PaymentSection({ tier, upgradeInfo, onSuccess, onCancel, token, api }) {
  const isUpgrade = !!upgradeInfo;
  const label = tier === 'premium_annual'
    ? 'WANTD+ Annual — $50/year'
    : isUpgrade
      ? `Upgrade to Lifetime — $${upgradeInfo?.charge?.toFixed(2) ?? '150.00'} (after $${upgradeInfo?.credit?.toFixed(2) ?? '0.00'} credit)`
      : 'WANTD+ Lifetime — $150 once';

  const [savedCards, setSavedCards] = useState(null); // null = loading
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [forceNewCard, setForceNewCard] = useState(false);
  const [setupSecret, setSetupSecret] = useState(null);
  const [setupError, setSetupError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load saved cards (and pre-select default if present)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/payment-methods');
        if (!alive) return;
        const cards = res.data || [];
        setSavedCards(cards);
        const def = cards.find(c => c.is_default) || cards[0];
        if (def) setSelectedCardId(def.id);
      } catch (e) {
        if (!alive) return;
        // Failure here is non-fatal — fall through to new-card flow
        setSavedCards([]);
      }
    })();
    return () => { alive = false; };
  }, [api]);

  // When user is on the new-card path, fetch a SetupIntent client_secret
  const showNewCardForm = savedCards !== null && (savedCards.length === 0 || forceNewCard);

  useEffect(() => {
    if (!showNewCardForm) return;
    if (setupSecret) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.post('/payment-methods/setup-intent');
        if (!alive) return;
        setSetupSecret(res.data.client_secret);
      } catch (e) {
        if (!alive) return;
        setSetupError(e.response?.data?.detail || 'Failed to start card setup.');
      }
    })();
    return () => { alive = false; };
  }, [showNewCardForm, setupSecret, api]);

  const callSubscribe = async (paymentMethodId) => {
    if (isUpgrade) {
      return axios.post(
        `${API_URL}/api/subscriptions/upgrade-lifetime`,
        { payment_method_id: paymentMethodId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
    return axios.post(
      `${API_URL}/api/subscriptions/subscribe`,
      { payment_method_id: paymentMethodId, tier },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const handleSavedCardPay = async () => {
    if (!selectedCardId) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await callSubscribe(selectedCardId);
      const data = response.data;
      if (data.status === 'active' || data.status === 'success') {
        onSuccess();
      } else if (data.client_secret) {
        // Backend asks for SCA confirmation on this PI
        const stripeJs = await stripePromise;
        const { error: confirmError } = await stripeJs.confirmCardPayment(data.client_secret, {
          payment_method: selectedCardId,
        });
        if (confirmError) {
          setError(confirmError.message || 'Payment failed.');
        } else {
          onSuccess();
        }
      } else {
        setError('Payment could not be processed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────

  // Loading saved-card list
  if (savedCards === null) {
    return (
      <div className="mt-4 p-4 rounded-lg bg-[var(--theme-bg)] border border-[var(--theme-border)] flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-[var(--theme-text-muted)]" />
      </div>
    );
  }

  // Saved-card path
  if (!showNewCardForm) {
    return (
      <div className="mt-4 space-y-4">
        <div className="p-3 rounded-lg bg-[#1E3A5F]/5 border border-[#1E3A5F]/20">
          <p className="text-sm font-semibold text-[#1E3A5F]">{label}</p>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-[var(--theme-text-muted)] uppercase tracking-wide">
            Pay with saved card
          </p>
          {savedCards.map(card => (
            <button
              key={card.id}
              type="button"
              onClick={() => setSelectedCardId(card.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                selectedCardId === card.id
                  ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                  : 'border-[var(--theme-border)] hover:border-[var(--theme-text-muted)]'
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-[var(--theme-text-secondary)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--theme-text-primary)]">
                    {brandLabel(card.brand)} •••• {card.last4}
                  </span>
                  {card.is_default && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Default
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--theme-text-muted)]">
                  Expires {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}
                </p>
              </div>
              {selectedCardId === card.id && (
                <Check className="w-4 h-4 text-[#1E3A5F] flex-shrink-0" />
              )}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setForceNewCard(true)}
          className="text-xs text-[#1E3A5F] hover:underline font-medium"
        >
          Use a different card
        </button>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 rounded-full h-11">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSavedCardPay}
            disabled={submitting || !selectedCardId}
            className="flex-1 bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-11"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pay & Activate'}
          </Button>
        </div>
        <p className="text-xs text-center text-[var(--theme-text-muted)]">Secured by Stripe. No refunds.</p>
      </div>
    );
  }

  // New-card path — wait for SetupIntent, then render Elements
  if (setupError) {
    return (
      <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-red-700 dark:text-red-400">{setupError}</p>
      </div>
    );
  }

  if (!setupSecret) {
    return (
      <div className="mt-4 p-4 rounded-lg bg-[var(--theme-bg)] border border-[var(--theme-border)] flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-[var(--theme-text-muted)]" />
      </div>
    );
  }

  const elementsOptions = {
    clientSecret: setupSecret,
    appearance: STRIPE_APPEARANCE,
  };

  return (
    <div className="mt-4">
      <Elements stripe={stripePromise} options={elementsOptions}>
        <NewCardForm
          label={label}
          onSubmitPM={async (paymentMethodId) => {
            const response = await callSubscribe(paymentMethodId);
            const data = response.data;
            if (data.status === 'active' || data.status === 'success') {
              onSuccess();
              return;
            }
            if (data.client_secret) {
              const stripeJs = await stripePromise;
              const { error: confirmError } = await stripeJs.confirmCardPayment(data.client_secret, {
                payment_method: paymentMethodId,
              });
              if (confirmError) throw new Error(confirmError.message || 'Payment failed.');
              onSuccess();
              return;
            }
            throw new Error('Payment could not be processed. Please try again.');
          }}
          onCancel={onCancel}
          onBackToSaved={savedCards.length > 0 ? () => { setForceNewCard(false); setSetupSecret(null); } : null}
        />
      </Elements>
    </div>
  );
}

function NewCardForm({ label, onSubmitPM, onCancel, onBackToSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Failed to save card.');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status !== 'succeeded' || !setupIntent.payment_method) {
      setError(`Card setup ended with status ${setupIntent?.status || 'unknown'}.`);
      setSubmitting(false);
      return;
    }

    try {
      await onSubmitPM(setupIntent.payment_method);
    } catch (err) {
      setError(err.message || err.response?.data?.detail || 'Payment failed.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 rounded-lg bg-[#1E3A5F]/5 border border-[#1E3A5F]/20">
        <p className="text-sm font-semibold text-[#1E3A5F]">{label}</p>
      </div>

      {onBackToSaved && (
        <button
          type="button"
          onClick={onBackToSaved}
          className="text-xs text-[#1E3A5F] hover:underline font-medium"
        >
          ← Use a saved card
        </button>
      )}

      <PaymentElement options={{ wallets: { link: 'never' } }} />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/40">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <p className="text-xs text-[var(--theme-text-muted)]">
        Your card is securely stored by Stripe and saved for future payments. WANTD never sees your card details.
      </p>

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting} className="flex-1 rounded-full h-11">
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-11"
          disabled={submitting || !stripe}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pay & Activate'}
        </Button>
      </div>
      <p className="text-xs text-center text-[var(--theme-text-muted)]">Secured by Stripe. No refunds.</p>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function PremiumPage() {
  const navigate = useNavigate();
  const { token, user, refreshUser, api } = useAuth();

  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [activePaymentTier, setActivePaymentTier] = useState(null); // null | "premium_annual" | "premium_lifetime"
  const [showUpgradePayment, setShowUpgradePayment] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pricingRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/pricing`),
        axios.get(`${API_URL}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTiers(pricingRes.data.tiers || []);
      setSubscription(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePaymentSuccess = async () => {
    setActivePaymentTier(null);
    setShowUpgradePayment(false);
    await refreshUser();
    await fetchData();
    setShowSuccessModal(true);
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure? You'll keep access until your annual period ends.")) return;
    setCanceling(true);
    try {
      await axios.post(`${API_URL}/api/subscriptions/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Subscription will be cancelled at end of billing period');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API_URL}/api/subscriptions/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Subscription reactivated!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const handleUpgradeClick = async () => {
    // Fetch pro-rata info before showing payment form
    try {
      // We'll pass the upgrade info after payment; for now just show the form
      // The backend calculates the credit during the upgrade call
      setUpgradeInfo({ charge: null, credit: null }); // null = backend will compute
      setShowUpgradePayment(true);
    } catch (err) {
      toast.error('Could not load upgrade pricing.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--theme-text-muted)]" />
        </div>
      </Layout>
    );
  }

  const currentTier = subscription?.subscription_tier || 'free';
  const isLifetime = currentTier === 'premium_lifetime';
  const isAnnual = currentTier === 'premium_annual' && subscription?.is_premium;
  const isCanceling = subscription?.cancel_at_period_end;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] flex items-center gap-2">
              <Crown className="w-6 h-6 text-[#C9956C]" />
              WANTD+
            </h1>
            <p className="text-[var(--theme-text-secondary)]">
              {isLifetime ? 'Lifetime member' : isAnnual ? 'Manage your subscription' : 'Choose a plan'}
            </p>
          </div>
        </div>

        {/* ── Lifetime Status ── */}
        {isLifetime && (
          <div className="p-5 rounded-2xl mb-6" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)' }}>
            <p className="text-[#C9956C] text-xs font-bold uppercase tracking-widest mb-1">LIFETIME ACCESS</p>
            <p className="text-white text-xl font-bold mb-1">You have WANTD+ forever</p>
            <p className="text-white/70 text-sm">No recurring charges. All features, always.</p>
          </div>
        )}

        {/* ── View invoices link (any paid tier) ── */}
        {(isLifetime || isAnnual) && (
          <button
            onClick={() => navigate('/account/billing')}
            className="mb-6 w-full text-left px-4 py-3 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] hover:border-[var(--theme-border-hover)] transition-colors flex items-center justify-between"
            data-testid="premium-view-invoices"
          >
            <span className="text-sm font-medium text-[var(--theme-text-primary)]">
              View invoices & billing history
            </span>
            <ArrowLeft className="w-4 h-4 rotate-180 text-[var(--theme-text-secondary)]" />
          </button>
        )}

        {/* ── Annual Status Banner ── */}
        {isAnnual && !isLifetime && (
          <div className={`p-4 rounded-xl mb-6 ${isCanceling ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-[#C9956C]/10 border border-[#C9956C]/30'}`}>
            <div className="flex items-start gap-3">
              {isCanceling ? (
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              ) : (
                <Crown className="w-5 h-5 text-[#C9956C] flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${isCanceling ? 'text-amber-600' : 'text-[#C9956C]'}`}>
                  {isCanceling ? 'Subscription Ending' : 'WANTD+ Annual — Active'}
                </p>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  {isCanceling
                    ? `Access ends ${formatDate(subscription.current_period_end)}`
                    : `Renews ${formatDate(subscription.current_period_end)}`}
                </p>
              </div>
              {isCanceling && (
                <Button
                  size="sm"
                  onClick={handleReactivate}
                  disabled={reactivating}
                  className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full"
                >
                  {reactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reactivate'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ── Tier Cards ── */}
        <div className="space-y-4 mb-6">
          {tiers.map((tier) => {
            const isCurrentTier = tier.id === currentTier;
            const isLifetierCard = tier.id === 'premium_lifetime';
            const isAnnualCard = tier.id === 'premium_annual';

            return (
              <div
                key={tier.id}
                className={`p-5 rounded-2xl border-2 bg-[var(--theme-card)] ${
                  isCurrentTier
                    ? isLifetime
                      ? 'border-[#1E3A5F]'
                      : 'border-[#C9956C]'
                    : isLifetierCard
                      ? 'border-[#C9956C]/40'
                      : 'border-[var(--theme-border)]'
                }`}
              >
                {/* Card header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-[var(--theme-text-primary)]">{tier.name}</h3>
                      {isCurrentTier && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#C9956C] text-white">Current</span>
                      )}
                      {tier.badge && !isCurrentTier && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#C9956C' }}>
                          {tier.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-3xl font-black text-[var(--theme-text-primary)]">
                        {tier.price === 0 ? 'Free' : `$${tier.price}`}
                      </span>
                      {tier.billing && (
                        <span className="text-sm text-[var(--theme-text-muted)]">
                          /{tier.billing === 'year' ? 'year' : 'once'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-1.5 mb-4">
                  {tier.features.map((f) => {
                    const Icon = ICON_MAP[f.icon] || Check;
                    return (
                      <li key={f.title} className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)]">
                        <Check className="w-4 h-4 text-[#059669] flex-shrink-0" />
                        {f.title}
                      </li>
                    );
                  })}
                </ul>

                {/* Actions */}
                {/* Free user: show subscribe options */}
                {!isAnnual && !isLifetime && !isCurrentTier && tier.id !== 'free' && (
                  <>
                    {activePaymentTier === tier.id ? (
                      <PaymentSection
                        tier={tier.id}
                        onSuccess={handlePaymentSuccess}
                        onCancel={() => setActivePaymentTier(null)}
                        token={token}
                        api={api}
                      />
                    ) : (
                      <Button
                        className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-xl h-11"
                        onClick={() => setActivePaymentTier(tier.id)}
                      >
                        {isAnnualCard ? 'Subscribe — $50/year' : 'Get Lifetime — $150'}
                      </Button>
                    )}
                  </>
                )}

                {/* Annual subscriber: upgrade to lifetime */}
                {isAnnual && isLifetierCard && !isLifetime && (
                  <>
                    {showUpgradePayment ? (
                      <PaymentSection
                        tier="premium_lifetime"
                        upgradeInfo={upgradeInfo}
                        onSuccess={handlePaymentSuccess}
                        onCancel={() => setShowUpgradePayment(false)}
                        token={token}
                        api={api}
                      />
                    ) : (
                      <Button
                        className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-xl h-11"
                        onClick={handleUpgradeClick}
                      >
                        Upgrade to Lifetime (pro-rata credit applied)
                      </Button>
                    )}
                  </>
                )}

                {/* Annual: cancel option */}
                {isAnnual && isAnnualCard && !isCanceling && !isLifetime && (
                  <Button
                    variant="outline"
                    className="w-full mt-2 rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                    onClick={handleCancel}
                    disabled={canceling}
                  >
                    {canceling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Cancel Subscription
                  </Button>
                )}

                {/* Lifetime: no cancel */}
                {isLifetime && isLifetierCard && (
                  <p className="text-xs text-center text-[var(--theme-text-muted)] mt-2">
                    Lifetime access — nothing to cancel.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="space-y-3">
          <h3 className="font-bold text-[var(--theme-text-primary)]">Frequently Asked Questions</h3>

          <details className="group p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
            <summary className="font-medium text-[var(--theme-text-primary)] cursor-pointer list-none flex items-center justify-between">
              What are Special Interests?
              <span className="text-[var(--theme-text-muted)] group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-sm text-[var(--theme-text-secondary)]">
              Special Interests let you notify sellers you are seriously interested in their item.
              Premium members get 5 free per week; non-premium users pay $2 each.
            </p>
          </details>

          <details className="group p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
            <summary className="font-medium text-[var(--theme-text-primary)] cursor-pointer list-none flex items-center justify-between">
              Can I cancel my annual subscription?
              <span className="text-[var(--theme-text-muted)] group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-sm text-[var(--theme-text-secondary)]">
              Yes — annual subscriptions can be cancelled anytime. You keep access until the end of your paid year.
              Lifetime plans are permanent and cannot be cancelled.
            </p>
          </details>

          <details className="group p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
            <summary className="font-medium text-[var(--theme-text-primary)] cursor-pointer list-none flex items-center justify-between">
              How does the lifetime upgrade credit work?
              <span className="text-[var(--theme-text-muted)] group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-sm text-[var(--theme-text-secondary)]">
              If you upgrade from Annual to Lifetime, we calculate how many days remain on your annual plan and
              apply a pro-rata credit. You only pay the difference between $150 and the unused portion of your $50 annual fee.
            </p>
          </details>

          <details className="group p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
            <summary className="font-medium text-[var(--theme-text-primary)] cursor-pointer list-none flex items-center justify-between">
              Are there refunds?
              <span className="text-[var(--theme-text-muted)] group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-sm text-[var(--theme-text-secondary)]">
              No refunds are offered on any tier. Annual subscriptions continue until the period ends; lifetime purchases are final.
            </p>
          </details>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-[var(--theme-card)] rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#C9956C] to-[#B8845B] flex items-center justify-center shadow-lg">
                <Crown className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                {isLifetime ? 'Lifetime Access Activated!' : 'Welcome to WANTD+!'}
              </h2>
              <p className="text-[var(--theme-text-secondary)]">
                {isLifetime ? 'You have full access forever — no more payments.' : 'All premium features are now active.'}
              </p>
            </div>
            <div className="space-y-2">
              <Button
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-xl"
                onClick={() => { setShowSuccessModal(false); navigate('/list/photos'); }}
              >
                <Camera className="w-4 h-4 mr-2" />
                Try AI Photo Scanner
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => { setShowSuccessModal(false); navigate('/'); }}
              >
                Browse Marketplace
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
